import { IMAGES_EXTENSION_TYPES } from '../consts'
import { InstallationFile } from '../models/file.model'
import { FileWithLatestStatus } from 'src/Libs/installation-statement/models'

/**
 * Filters files by name and description
 * @param files installation files
 * @param searchString
 * @returns filtered files
 */
export const filterFiles = (files: InstallationFile[], searchString: string): InstallationFile[] => {
  return files?.filter(
    file =>
      file.description?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
      file.name?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase())
  )
}

/**
 * Validates if the a singular file matches to a given search string
 * @param fileName
 * @returns true if file fits to the given searchString
 */
export const validateFileToSearchString = (fileName: string, searchString: string): boolean => {
  return fileName.toLowerCase().search(searchString.toLowerCase()) !== -1
}

/**
 * Create a link click which opens the chosen document url
 * @param url document url
 * @param extension document extension
 * @param newPage wheter to open in new tab
 */
export const signalDownload = (url: string, extension: string, newPage?: boolean): void => {
  const link = document.createElement('a')
  link.href = url
  link.download = extension
  if (newPage) {
    link.target = '_blank'
  }
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const loadFilePreviews = async (
  installationId: number,
  getInstallationFiles: (installationId?: number, storageDeviceLinkId?: number, hasValidStatement?: boolean) => Promise<FileWithLatestStatus[]>,
  previewFile: (installationId: number, file: FileWithLatestStatus) => Promise<string | undefined>,
  storageDeviceLinkId?: number,
  hasValidStatement?: boolean
): Promise<FileWithLatestStatus[]> => {
  const installationFiles = await getInstallationFiles(installationId, storageDeviceLinkId, hasValidStatement)
  const filesWithThumbnails = await Promise.all(
    installationFiles.map(async file => {
      const shouldFetchImageUrl = file.extension && IMAGES_EXTENSION_TYPES.includes(file.extension)
      const result = { ...file }

      if (shouldFetchImageUrl) {
        const imageUrl = await previewFile(installationId, file)
        result.imageUrl = imageUrl
      }

      return result
    })
  )

  return filesWithThumbnails
}

/** Put 'summary' over 'warranty', then by 'id' for equal files (higher id means newer), the rest is alphabetical */
export const sortInstallationFiles = (files: FileWithLatestStatus[]): FileWithLatestStatus[] => {
  return files?.slice().sort((a, b) => {
    const { type: tagA, id: idA } = a
    const { type: tagB, id: idB } = b
    const warrantyTag = tagB === 'warranty' ? idB - idA : -1
    if (tagA === 'summary') return tagB === 'summary' ? idB - idA : -1
    if (tagA === 'warranty') return tagB === 'summary' ? 1 : warrantyTag
    return tagB === 'summary' || tagB === 'warranty' ? 1 : tagA.localeCompare(tagB)
  })
}
